/**
 * THE FOUR THINGS AN OWNER DOES TO THE LIST — add a step, remove one, move one, change
 * one — as pure functions over a spine.
 *
 * PURE. No Vue, no store, no I/O. Every function takes a spine and returns a NEW spine;
 * nothing is mutated, so a page can compare the two and a spec can walk every one of them
 * without mounting anything.
 *
 * ── WHY THESE ARE NOT IN THE COMPONENT ────────────────────────────────────────────────
 * Position IS `next` (`helpers/task-spine.js`), so moving a row is a real behavioural edit
 * to the graph rather than a re-sort of some cards. That was worth making impossible to get
 * wrong in a template: `moveProblem` below refuses a move by compiling the list the move
 * would produce and asking `localProblems` — the same client mirror of `validate.ts` the
 * composer's gate already uses — rather than by a second, hand-written idea of the rule.
 *
 * ── AND WHY REMOVING A STEP DOES NOT REPAIR THE LINKS INTO IT ─────────────────────────
 * A jump to a step that is gone is carried through untouched, exactly as `task-spine.js`
 * carries it: `localProblems` reports it as `dangling`, and `TaskStepLink.vue` already
 * draws "A step that is no longer here ({id})" so a deleted target never reads as "finish
 * here". Silently re-pointing somebody's branch at whatever happens to be next is how a
 * delete becomes a change to what the assistant says to a customer.
 */
import {
  CARRY_ON,
  HAND_OVER,
  HAND_OVER_NAME,
  spineToDefinition,
} from './task-spine.js';
import {
  localProblems,
  mintStepId,
  stepToWire,
  toStepRow,
} from './task-flow-draft.js';
import { carriesQuestion, shapeForRow } from './task-answer-kinds.js';
import { isUploadStep, withoutContactSave } from './task-uploads.js';
import {
  deriveSaveKey,
  instructionFor,
  nameFor,
  questionFieldFor,
  questionLangOf,
  wasGenerated,
} from './task-question.js';

/** The four kinds an owner picks from. `end` is not one: the end of the list is the end. */
export const STEP_KINDS = Object.freeze([
  'question',
  'tell',
  'call',
  'handoff',
]);

/** Which step `type` a picked kind writes. A question starts as the plainest of the eight. */
const TYPE_FOR_KIND = {
  question: 'ask',
  tell: 'tell',
  call: 'call',
  handoff: 'handoff',
};

const rowsOf = spine => (spine && Array.isArray(spine.rows) ? spine.rows : []);

const idsIn = spine =>
  rowsOf(spine)
    .map(row => row?.step?.id)
    .filter(id => typeof id === 'string');

/** A blank option, with an id nothing on this step is using. */
export const blankOption = (taken = []) => ({
  option: { id: mintStepId(taken, 'o'), labelEn: '', labelAr: '' },
  then: CARRY_ON,
});

/**
 * A blank row of one kind, ready to be filled in.
 *
 * EVERY ONE OF THEM IS DELIBERATELY INCOMPLETE. A blank `ask` carries no `saveTo` at all
 * rather than `{chat: ''}`: an empty save key is `bad_save_key`, which reads as something
 * the owner typed wrong, and there is nothing there to have typed. The key arrives the
 * moment the question does (`helpers/task-question.js`).
 *
 * A live list opens on "hand the chat to a person" and an action opens on the same, because
 * that is the answer an owner nearly always wants and the one the design says he must never
 * have to discover the words `ifNone` or `onFailed` to give.
 */
export const blankRow = (kind, taken = []) => {
  const id = mintStepId(taken);
  const type = TYPE_FOR_KIND[kind] ?? 'ask';
  if (type === 'tell') {
    return {
      step: { id, name: '', type: 'tell', instruction: '' },
      then: CARRY_ON,
      clears: null,
    };
  }
  if (type === 'call') {
    return {
      step: {
        id,
        name: '',
        type: 'call',
        connection: '',
        inputs: {},
        keep: {},
      },
      then: CARRY_ON,
      ifFailed: HAND_OVER,
      clears: null,
    };
  }
  if (type === 'handoff') {
    return {
      step: { id, name: HAND_OVER_NAME, type: 'handoff', note: '' },
      then: CARRY_ON,
      clears: null,
    };
  }
  return {
    step: { id, name: '', type: 'ask', instruction: '', kind: 'text' },
    then: CARRY_ON,
    clears: null,
  };
};

/** Insert a new row at `index` (its row number, 0 = the top). Returns the spine and the id. */
export const addRowAt = (spine, index, kind) => {
  const rows = rowsOf(spine);
  // `fresh`: made in this sitting, so a yes/no that confirms a change reads it back by
  // default (P-10.2). Never saved: the compiler reads only `step`, `then` and the arms.
  const row = { ...blankRow(kind, idsIn(spine)), fresh: true };
  const at = Math.max(0, Math.min(index, rows.length));
  return {
    spine: { ...spine, rows: [...rows.slice(0, at), row, ...rows.slice(at)] },
    id: row.step.id,
  };
};

export const removeRowAt = (spine, index) => {
  const rows = rowsOf(spine);
  if (index < 0 || index >= rows.length) return spine;
  return { ...spine, rows: rows.filter((_, at) => at !== index) };
};

export const replaceRowAt = (spine, index, row) => {
  const rows = rowsOf(spine);
  if (index < 0 || index >= rows.length) return spine;
  return { ...spine, rows: rows.map((old, at) => (at === index ? row : old)) };
};

/** Swap a row with its neighbour. Out of range in either direction is a no-op, not a throw. */
export const moveRowAt = (spine, index, delta) => {
  const rows = rowsOf(spine);
  const to = index + delta;
  if (index < 0 || index >= rows.length || to < 0 || to >= rows.length) {
    return spine;
  }
  const next = [...rows];
  [next[index], next[to]] = [next[to], next[index]];
  return { ...spine, rows: next };
};

/**
 * ARE THESE TWO GRAPHS THE SAME GRAPH? — and it is not `JSON.stringify`.
 *
 * ── THE BUG THIS EXISTS FOR, WHICH A ROUND-TRIP SPEC CANNOT SEE ───────────────────────
 * `spineToDefinition` rebuilds a step by copying its body and then WRITING the links and
 * the options back on, so `options` ends up after `saveTo` where the stored step had it
 * before. Same graph, same values, different key order — and `JSON.stringify` is
 * order-sensitive.
 *
 * `specs/task-spine-roundtrip.spec.js` compares with `toStrictEqual`, which is not, so it
 * is green either way. What is NOT green either way is the composer's dirty check: a task
 * would be "changed" the instant it loaded, every rename would carry a `definition`, and
 * the server bumps `version` only on a real change — so a title edit would look like a
 * graph edit. It is caught here by one spec and by one sentence in the page.
 *
 * Stable order, and a key whose value is `undefined` is absent, exactly as `JSON.stringify`
 * treats it — so a graph that came off the wire and a graph this builder just compiled
 * compare equal when they mean the same thing, and only then.
 */
const stable = value => {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (value && typeof value === 'object') {
    const body = Object.keys(value)
      .sort()
      .filter(name => value[name] !== undefined)
      .map(name => `${JSON.stringify(name)}:${stable(value[name])}`)
      .join(',');
    return `{${body}}`;
  }
  return value === undefined ? 'null' : JSON.stringify(value);
};

export const sameGraph = (a, b) => stable(a) === stable(b);

const stamp = problem =>
  `${problem.stepId}:${problem.problem}:${problem.detail ?? ''}`;

/**
 * The connection input this move would break, or `null`.
 *
 * "The calendar needs the studio, and step 3 is where it's answered" is the `input_before_save`
 * rule — the commonest problem on a connection card and the one `arrivingKeys` already
 * computes. Rather than write a second version of it, this compiles the list the move WOULD
 * produce and asks the same `localProblems` the composer's gate asks, then reports only
 * what is NEW. A task that already had the problem is not refused a move because of it.
 *
 * @returns {{stepId: string, detail: string}|null}
 */
export const moveProblem = (spine, index, delta) => {
  const moved = moveRowAt(spine, index, delta);
  if (moved === spine) return null;
  const was = new Set(
    localProblems(spineToDefinition(spine))
      .filter(problem => problem.problem === 'input_before_save')
      .map(stamp)
  );
  const gained = localProblems(spineToDefinition(moved)).find(
    problem =>
      problem.problem === 'input_before_save' && !was.has(stamp(problem))
  );
  return gained ? { stepId: gained.stepId, detail: gained.detail ?? '' } : null;
};

// ── the one box, and the eight rows under it ──────────────────────────────────────────

/**
 * Fields `helpers/task-spine.js` owns on the ROW rather than on the step. A reshape must
 * never leave one of them on the step body, or the same link would exist in two places and
 * the list and the graph could disagree about where a customer goes next.
 */
const SPINE_OWNED = [
  'next',
  'clears',
  'options',
  'ifNone',
  'onWorked',
  'onFailed',
];

/**
 * The text in the one box, whichever field this type keeps it in.
 *
 * A live list saved before P-10.24 keeps its words in `instruction` (the box wrote there), so
 * with no question yet the box shows those words rather than an empty box. Typing then writes
 * the question, and the legacy rule keeps her instruction as her own.
 */
export const needTextOf = (step, lang = 'en') => {
  const field = questionFieldFor(step?.type, lang);
  const own = step?.[field] ?? '';
  if (own.trim() !== '') return own;
  // The legacy live list's words are English prose, and only while NO question is written.
  const unwritten =
    (step?.questionEn ?? '').trim() === '' &&
    (step?.questionAr ?? '').trim() === '';
  return step?.type === 'choose_live' && lang === 'en' && unwritten
    ? (step.instruction ?? '')
    : '';
};

/**
 * The step body, re-shaped for a new type.
 *
 * `toStepRow` + `stepToWire` rather than a hand-written switch, because that pair already
 * carries the rule this cannot afford to get wrong: a field belonging to the type the step
 * USED to be is dropped, and a field this builder has never heard of — from a newer sidecar
 * — rides along untouched on `source`.
 *
 * FOUR THINGS IT HAS TO DO ON TOP, and all are `.strict()` refusals rather than untidiness:
 *   • the spine-owned links come off, because the ROW carries them;
 *   • `questionEn`/`questionAr`/`asWritten`/`ifStuck`/`optional` come off anything that is not a
 *     question (`ask`, `choose` and, since P-10.24, `choose_live`). They are not in
 *     `stepToWire`'s owned table at all — they live on `source` and ride along;
 *   • `labelFields` comes off anything that is not a live list;
 *   • P-10.2: `readBackOf` (and `readBackKeep`) comes off anything that is not a list of options, and
 *     `summaryOf`/`summaryKeep` off anything that is not a message. Once the step changes
 *     type the switch that set them is hidden, so the owner could not clear them either.
 *     The sidecar's schema is `.strict()`, which REFUSES an unknown key rather than dropping
 *     it. Carrying one across a switch would make the whole task unsaveable, on a screen that
 *     looked fine.
 */
export const stepBodyFrom = draft => {
  const wire = stepToWire(draft);
  SPINE_OWNED.forEach(field => delete wire[field]);
  if (!carriesQuestion(wire.type)) {
    delete wire.questionEn;
    delete wire.questionAr;
    // P-10.24 and P-10.25: only a question has a way to be sent or a way out.
    delete wire.asWritten;
    delete wire.ifStuck;
    // F1.3: and only a question can be optional.
    delete wire.optional;
  }
  // P-10.24: per-language row names belong to a live list alone.
  if (wire.type !== 'choose_live') delete wire.labelFields;
  // Services G2.4: limits belong to a photo or file question alone, and a typed-answer rule never
  // does (nothing is typed).
  if (isUploadStep(wire)) delete wire.rule;
  else delete wire.uploads;
  // P-10.2: a read-back belongs to a list of options, a summary to a message.
  // P-4.1 / P-4.2: `send` belongs to a message, too.
  if (wire.type !== 'choose') {
    delete wire.readBackOf;
    // Services task review E: and what it adds from earlier Calls.
    delete wire.readBackKeep;
  }
  // Field fixes G2.3: "They may pick more than one" belongs to a list of options alone.
  if (wire.type !== 'choose') delete wire.many;
  if (wire.type !== 'tell') {
    delete wire.summaryOf;
    delete wire.summaryKeep;
    delete wire.send;
  }
  return wire;
};

const reshaped = (step, type, kind) => {
  const draft = toStepRow(step);
  draft.type = type;
  if (kind !== undefined) draft.kind = kind;
  return stepBodyFrom(draft);
};

/**
 * Write the three generated strings from the question, each under the legacy rule.
 *
 * `previous` is the question that is being replaced. Anything that is not byte-for-byte
 * what this file would have produced for THAT question is somebody's writing and is kept —
 * see `helpers/task-question.js`. `regenerateInstruction` forces the instruction's hand for
 * the one case the byte test cannot see: a step that has just changed type, where the old
 * instruction was the old box's own contents.
 */
const withGenerated = (step, said, previous, taken, regenerateInstruction) => {
  const next = { ...step };
  const type = step.type;
  if (carriesQuestion(type)) {
    if (
      regenerateInstruction ||
      wasGenerated(step.instruction, instructionFor(type, previous))
    ) {
      next.instruction = instructionFor(type, said);
    }
  }
  if (wasGenerated(step.name, nameFor(previous))) next.name = nameFor(said);
  if (said.trim() !== '') {
    const own = step.saveTo?.chat ?? '';
    if (own === '' || own === deriveSaveKey(previous, taken)) {
      next.saveTo = {
        ...(step.saveTo ?? {}),
        chat: deriveSaveKey(said, taken),
      };
    }
  }
  return next;
};

/**
 * The row after the owner has typed in the one box.
 *
 * @param {object} row   a spine row
 * @param {string} said  what is in the box now
 * @param {string[]} taken  every save key the REST of the task writes
 */
export const withNeedText = (row, said, taken = [], lang = 'en') => {
  const step = row?.step ?? {};
  const field = questionFieldFor(step.type, lang);
  const previous = step[field] ?? '';
  const written = { ...step, [field]: said };
  return { ...row, step: withGenerated(written, said, previous, taken, false) };
};

/**
 * The row after the owner has moved the dot in "What comes back?".
 *
 * The SENTENCE SURVIVES THE SWITCH. Whatever is in the box is written into whichever field
 * the new type keeps it in, so moving from "Anything they type" to "One of these — from
 * your system" does not empty the screen the owner was looking at. That is the whole point
 * of the inversion: he is writing a question, and the field it lands in is our business.
 */
export const withAnswerRow = (row, answerRowId, taken = []) => {
  const shape = shapeForRow(answerRowId);
  if (!shape) return row;
  const step = row?.step ?? {};
  if (
    step.type === shape.type &&
    (shape.kind === undefined || step.kind === shape.kind)
  ) {
    return row;
  }
  // P-7.1: the question moves across in the language it is written in.
  const lang = questionLangOf(step);
  const said = needTextOf(step, lang);
  const wasBoxed = !carriesQuestion(step.type);
  const body = reshaped(step, shape.type, shape.kind);
  body[questionFieldFor(shape.type, lang)] = said;
  const generated = withGenerated(body, said, said, taken, wasBoxed);
  const next = {
    ...row,
    // Services G2.4: a photo or file is links, so it is never saved on the contact.
    step: isUploadStep(generated) ? withoutContactSave(generated) : generated,
  };

  // The row-level links each shape needs, and none it does not.
  delete next.options;
  delete next.ifNone;
  delete next.ifFailed;
  if (shape.type === 'choose') {
    next.options =
      Array.isArray(row.options) && row.options.length
        ? row.options
        : [blankOption([]), blankOption(['o1'])];
  }
  // A live list MUST say where a customer goes when nothing comes back (`ifNone` is
  // required by `taskDefinitionSchema`), and "hand the chat to a person" is the answer
  // nobody should have to learn a word to give.
  if (shape.type === 'choose_live') next.ifNone = row.ifNone ?? HAND_OVER;
  return next;
};

export default blankRow;
